import React, { useState } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "./ImageCarousel.css";

/**
 * Hero carousel.
 *
 * Source images range from 1.67:1 to 2.49:1, so the previous fixed 500px height
 * with `object-fit: fill` stretched every one of them by a different amount.
 * The slide now owns a fixed aspect ratio and the image uses `cover`, which
 * crops instead of distorting.
 *
 * Paths are absolute ("/img1.jpg"): the old relative form resolved against the
 * current route and broke on any nested URL.
 */
const CAROUSEL_ITEMS = [
  {
    img: "/img1",
    width: 1920,
    height: 1076,
    // Alt text describes the image for screen readers and image search; the
    // old "Slide 1" / "Slide 2" told neither anything.
    alt: "Illustration of a human head formed from circuit traces, representing applied artificial intelligence",
    heading: "Innovating with Intelligence",
    description:
      "We leverage AI to enhance decision-making and create market-leading solutions that drive success.",
  },
  {
    img: "/s3",
    width: 1500,
    height: 900,
    alt: "Engineering team collaborating around a workstation on a staffing engagement",
    heading: "Empowering Teams with Expert Staffing",
    description:
      "Our staffing solutions bring top-tier professionals to your projects, ensuring excellence and innovation.",
  },
  {
    img: "/fullstack3",
    width: 1400,
    height: 732,
    alt: "Developer writing full stack application code across front-end and back-end files",
    heading: "Innovating Across the Stack",
    description:
      "We offer a complete suite of full-stack services, combining front-end creativity with back-end strength to meet client needs.",
  },
  {
    img: "/CV2",
    width: 1737,
    height: 980,
    alt: "Computer vision system detecting and tracking objects in a live camera feed",
    heading: "Building a Smarter World Through Vision",
    description:
      "Our Computer Vision technologies enhance image processing, delivering high-impact solutions that drive innovation.",
  },
  {
    img: "/cad",
    width: 1366,
    height: 549,
    alt: "CAD model of a mechanical assembly being generated automatically",
    heading: "Streamlining the Future of Design",
    description:
      "With CAD automation, we optimize every step of the design process, reducing errors and accelerating project timelines.",
  },
  {
    img: "/image4",
    width: 682,
    height: 408,
    alt: "Digital marketing performance dashboard showing campaign engagement metrics",
    heading: "Strategic Digital Solutions for a Competitive Edge",
    description:
      "Our innovative digital marketing approaches enhance brand visibility and engagement, positioning your business ahead in the evolving digital world.",
  },
];

function CarouselComponent() {
  const [currentIndex, setCurrentIndex] = useState(0);

  return (
    <section className="carousel-hero" aria-label="Our services">
      <Carousel
        showArrows
        autoPlay
        infiniteLoop
        stopOnHover
        swipeable
        emulateTouch
        showThumbs={false}
        showStatus={false}
        interval={6000}
        transitionTime={600}
        onChange={setCurrentIndex}
        selectedItem={currentIndex}
        renderArrowPrev={(onClickHandler, hasPrev, label) =>
          hasPrev && (
            <button
              type="button"
              onClick={onClickHandler}
              aria-label={label}
              className="carousel-hero__arrow carousel-hero__arrow--prev"
            />
          )
        }
        renderArrowNext={(onClickHandler, hasNext, label) =>
          hasNext && (
            <button
              type="button"
              onClick={onClickHandler}
              aria-label={label}
              className="carousel-hero__arrow carousel-hero__arrow--next"
            />
          )
        }
      >
        {CAROUSEL_ITEMS.map((item, index) => (
          <div key={item.img} className="carousel-hero__slide">
            <picture>
              <source srcSet={`${item.img}.webp`} type="image/webp" />
              <img
                src={`${item.img}.jpg`}
                alt={item.alt}
                width={item.width}
                height={item.height}
                className="carousel-hero__img"
                /* The first slide is the largest-contentful paint element, so
                   it loads eagerly and at high priority; the rest wait. */
                loading={index === 0 ? "eager" : "lazy"}
                fetchpriority={index === 0 ? "high" : "low"}
                decoding={index === 0 ? "sync" : "async"}
              />
            </picture>

            <div className="carousel-hero__overlay">
              <div className="carousel-hero__text">
                {/* h2, not h1 — the page keeps a single h1 for its own title. */}
                <h2 className="carousel-hero__heading">{item.heading}</h2>
                <p className="carousel-hero__desc">{item.description}</p>
              </div>
            </div>
          </div>
        ))}
      </Carousel>
    </section>
  );
}

export default CarouselComponent;
